import React, { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import momentTz from 'moment-timezone';
import { capitalize, get } from 'lodash';
import compose from 'utils/compose';
import { reaction } from 'mobx';
import { observer, inject } from 'mobx-react';
import { computeDestinationPoint } from 'geolib';
import DeckGL, { IconLayer, ScatterplotLayer } from 'deck.gl';
import { Map as ReactMapGL, Popup } from 'react-map-gl/maplibre';
import WebMercatorViewport from '@math.gl/web-mercator';
import { Box, Grid, Container } from '@mui/material';
import CAR_MARKERS from 'assets/svg/car.svg';
import HOME_MARKERS from '../../assets/svg/home_marker.svg';
import PACKAGE_MARKERS from '../../assets/svg/package_marker.svg';
import POD_MARKERS from '../../assets/svg/POD_pin.svg';

import { isoToLocalHuman } from '../../utils/calendar';
import { resolveDeliveryWindow } from '../../utils/deliveryDate';
import * as SHIPMENT_STATUS from '../../constants/shipmentStatus';
import { DRIVER_LOCATION_PIN_REFRESH_INTERVAL, DRIVER_LOCATION_RADIUS_REFRESH_INTERVAL } from 'constants/common';
import ClickTooltip from '../ClickTooltip';
import ToggleMapPins from './ToggleMapPins';

const mapStyle = `https://api.maptiler.com/maps/topo/style.json?key=${encodeURIComponent(import.meta.env.REACT_APP_MAP_TILER_KEY)}`;

const INITIAL_VIEW_STATE = {
  latitude: 37.772402,
  longitude: -122.414265,
  zoom: 15,
  width: 400,
  height: 400,
  maxZoom: 16,
  minZoom: 5,
};

function Map(props) {
  const { mediaQuery, classes, milestone, store } = props;
  const { deliveryStore, podStore } = store;
  const { delivery, driverLocationDetail, settings, token } = deliveryStore;
  const { driver, shipment, dropoff, pickup, zip } = delivery;

  const [viewport, setViewport] = useState(INITIAL_VIEW_STATE);
  const [isModifiedViewState, setIsModifiedViewState] = useState(false);
  const [openEcoTooltip, setOpenEcoTooltip] = useState(false);
  const [images, setImages] = useState([]);
  const [selectedPOD, setSelectedPOD] = useState({});
  const [packageLocationPin, setPackageLocationPin] = useState(null);
  const [coordinates, setCoordinates] = useState({
    latitude: INITIAL_VIEW_STATE.latitude,
    longitude: INITIAL_VIEW_STATE.longitude,
  });
  const [enableRedPastDate, setEnableRedPastDate] = useState(false);
  const deckRef = useRef(null);

  const isEcoFriendly = !!delivery.eco_friendly;
  const isFar = driverLocationDetail?.is_driver_far;
  const driverLocation = driverLocationDetail?.driver_location;
  const distanceInMeters = driverLocationDetail?.driver_distance;
  const showDriverLocation = settings?.public_tracking_driver === 'true' || Boolean(token);
  const isAuthenticated = Boolean(token);

  useEffect(() => {
    const highlightPastDate = get(deliveryStore, 'settings.highlight_past_delivery_date');
    setEnableRedPastDate(highlightPastDate === 'true');
  }, [deliveryStore]);

  useEffect(() => {
    // Initialize local state from existing store values
    setImages(podStore.imagesPOD);
    setPackageLocationPin(deliveryStore.packageLocation);

    const disposeImagesPOD = reaction(
      () => podStore.imagesPOD,
      () => setImages(podStore.imagesPOD),
    );

    const disposePackageLocation = reaction(
      () => deliveryStore.packageLocation,
      () => setPackageLocationPin(deliveryStore.packageLocation),
    );

    return () => {
      disposeImagesPOD();
      disposePackageLocation();
    };
  }, [podStore, deliveryStore]);

  const handleViewStateChange = (viewStateChange) => {
    const { viewState, oldViewState } = viewStateChange;

    setViewport(viewState);
    if (oldViewState.zoom && viewState.zoom !== oldViewState.zoom && isModifiedViewState === false) setIsModifiedViewState(true);
  };

  const fitMap = useCallback(
    (from, to) => {
      if (isModifiedViewState) return;

      const dx = Math.max(Math.abs(from.latitude - to.latitude), 0.002);
      const dy = Math.max(Math.abs(from.longitude - to.longitude), 0.002);

      if (!viewport.width) return;

      const wmv = new WebMercatorViewport(viewport);
      const { longitude, latitude, zoom } = wmv.fitBounds(
        [
          [from.longitude - dy, from.latitude - dx],
          [from.longitude + dy, from.latitude + dx],
        ],
        { padding: 100 },
      );

      setViewport({
        ...viewport,
        longitude,
        latitude,
        zoom,
        transitionDuration: 200,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [coordinates, driverLocation, isModifiedViewState],
  );

  useEffect(() => {
    if (!isAuthenticated) {
      if (zip?.longitude != null && zip?.latitude != null) {
        setCoordinates({
          longitude: zip.longitude,
          latitude: zip.latitude,
        });
      }
    } else if (shipment?.dropoff_address?.lng != null && shipment?.dropoff_address?.lat != null) {
      setCoordinates({
        longitude: shipment.dropoff_address.lng,
        latitude: shipment.dropoff_address.lat,
      });
    }
  }, [isAuthenticated, zip, shipment]);

  useEffect(() => {
    const from = coordinates;
    const distance = Number(distanceInMeters);
    const to = driverLocation
      ? { longitude: driverLocation.longitude, latitude: driverLocation.latitude }
      : Number.isFinite(distance) && distance > 0
        ? computeDestinationPoint(from, distance, 0)
        : null;

    if (from && to) fitMap(from, to);
  }, [coordinates, driverLocation, distanceInMeters, fitMap]);

  useEffect(() => {
    if (!coordinates) return;

    setViewport((current) => ({ ...current, longitude: coordinates.longitude, latitude: coordinates.latitude }));
  }, [coordinates]);

  useEffect(() => {
    const shouldRefresh = isFar === false || driverLocation;
    if (!shouldRefresh) return;

    const interval = isFar ? DRIVER_LOCATION_PIN_REFRESH_INTERVAL : DRIVER_LOCATION_RADIUS_REFRESH_INTERVAL;

    const timerId = setInterval(() => {
      deliveryStore.getDriverLocation();
    }, interval);

    return () => {
      clearInterval(timerId);
    };
  }, [deliveryStore, driverLocation, isFar]);

  const handleOpenEcoTooltip = () => {
    if (!isEcoFriendly) {
      return;
    }

    setOpenEcoTooltip(true);
  };

  const renderMarker = () => {
    if (!coordinates) return;

    return new IconLayer({
      id: 'shipment-marker',
      data: [{ location: [coordinates.longitude, coordinates.latitude], size: 40, icon: 'green' }],
      pickable: true,
      iconAtlas: HOME_MARKERS,
      iconMapping: { green: { x: 0, y: 0, width: 168, height: 228, anchorY: 228 } },
      getIcon: (d) => d.icon,
      sizeScale: 1,
      opacity: 1,
      visible: true,
      billboard: false,
      getPosition: (d) => d.location,
      getSize: (d) => d.size,
    });
  };

  const renderDriverLocation = () => {
    if (!showDriverLocation) return;

    if (driverLocation) {
      const iconData = [{ size: 20, location: [driverLocation.longitude, driverLocation.latitude] }];
      const iconMapping = { white: { x: 0, y: 50, width: 100, height: 50, anchorY: 25 } };
      const angle = driverLocation.heading ? (driverLocation.heading < 90 ? 90 - driverLocation.heading : 450 - driverLocation.heading) : 90;

      return new IconLayer({
        id: 'driver-location',
        data: iconData,
        pickable: false,
        iconAtlas: CAR_MARKERS,
        iconMapping: iconMapping,
        getIcon: () => 'white',
        sizeScale: 1,
        opacity: 1,
        visible: true,
        billboard: false,
        getPosition: (data) => data.location,
        getSize: (data) => data.size,
        getAngle: () => angle,
      });
    }

    if (isFar === false && distanceInMeters > 0 && coordinates) {
      return new ScatterplotLayer({
        id: 'driver-location-radius',
        data: [{ coordinates: [coordinates.longitude, coordinates.latitude] }],
        pickable: false,
        opacity: 0.1,
        stroked: true,
        filled: true,
        radiusMinPixels: 1,
        lineWidthMinPixels: 1,
        getPosition: (d) => d.coordinates,
        getRadius: distanceInMeters,
        getFillColor: () => [74, 158, 255],
        getLineColor: () => [0, 0, 0],
      });
    }
  };

  const handleSelectPOD = (info) => {
    if (!info) return null;

    if (info.picked) {
      setSelectedPOD(info);
    } else {
      setSelectedPOD({});
    }
  };

  const handleMapClick = (event) => {
    if (!deckRef.current) return;

    const picked = deckRef.current.pickObject({
      x: event.point.x,
      y: event.point.y,
      radius: 5,
    });

    if (picked && picked.layer?.id === 'image-pod-layer') {
      handleSelectPOD({ ...picked, picked: true });
    } else {
      setSelectedPOD({});
    }
  };

  const renderImagesPOD = () => {
    return new IconLayer({
      id: 'image-pod-layer',
      data: Array.isArray(images) ? images.map((img) => ({
        location: [img?.geolocation?.longitude, img?.geolocation?.latitude],
        size: 48,
        icon: 'green',
        url: img?.url,
      })) : [],
      getIcon: (d) => d.icon,
      getSize: (d) => d.size,
      pickable: true,
      iconAtlas: POD_MARKERS,
      iconMapping: { green: { x: 0, y: 0, width: 47, height: 61, anchorY: 61 } },
      getPosition: (d) => d.location,
      onClick: (info) => handleSelectPOD(info),
    });
  };

  const renderPackageLocationPOD = () => {
    return new IconLayer({
      id: 'image-package-location-layer',
      data: [
        {
          location: images && images.length > 0 ? [packageLocationPin?.longitude, packageLocationPin?.latitude] : [],
          size: 48,
          icon: 'green',
        },
      ],
      getIcon: (d) => d.icon,
      getSize: (d) => d.size,
      pickable: true,
      iconAtlas: PACKAGE_MARKERS,
      iconMapping: {
        green: { x: 0, y: 0, width: 64, height: 83, anchorY: 83 },
      },
      getPosition: (d) => d.location,
    });
  };

  const layers = [...(isAuthenticated ? [renderDriverLocation(), renderMarker()] : []), renderPackageLocationPOD(), renderImagesPOD()].filter(
    Boolean,
  );

  let etaInTime = '';
  let isPastDate = false;
  const inProgress = pickup && pickup.status === 'SUCCEEDED';
  const isDelivered = milestone === SHIPMENT_STATUS.DELIVERED;

  if (dropoff && dropoff.status === 'SUCCEEDED') {
    etaInTime = 'Delivered';
  } else if (dropoff && (dropoff.status === 'FAILED' || dropoff.status === 'DISCARDED')) {
    etaInTime = '-';
  } else if (SHIPMENT_STATUS.FAILED === milestone) {
    etaInTime = '-';
  } else if (inProgress && dropoff.estimated_arrival_ts) {
    const eta = moment(dropoff.estimated_arrival_ts).diff(moment.now());
    if (eta < 0) {
      etaInTime = '-';
    } else {
      const etaInSeconds = Math.ceil(eta / 1000);
      const etaInMinutes = Math.ceil(etaInSeconds / 60);
      const roundUp = Math.max(0, Math.floor((etaInMinutes + 4) / 5) * 5);
      const minutes = roundUp % 60;
      const hours = Math.floor(roundUp / 60);
      etaInTime = `about ${hours > 0 ? `${hours}h ` : ''}` + minutes + 'm';
    }
  } else if ([SHIPMENT_STATUS.UNSERVICEABLE].includes(milestone)) {
    etaInTime = 'N/A';
  } else {
    const timezone = shipment.timezone || 'America/Los_Angeles';
    const { dropoff_latest_ts: dropoffLatestTs } = resolveDeliveryWindow(shipment, delivery.outbound_events, delivery.no_service_dates);

    etaInTime = 'by ' + isoToLocalHuman(dropoffLatestTs, timezone, dropoffLatestTs, 'hA MM/DD/YYYY');
    if (settings.hide_delivery_datetime === 'true' && (SHIPMENT_STATUS.PROCESSING === milestone || SHIPMENT_STATUS.PROCESSED === milestone)) {
      etaInTime = 'N/A';
    }

    // Check if the delivery date is in the past
    const now = momentTz().tz(timezone);
    const dropoffLatest = momentTz(dropoffLatestTs).tz(timezone);
    isPastDate = !isDelivered && dropoffLatest.isBefore(now);
  }

  const shouldHighlightDate = enableRedPastDate && isPastDate;

  function renderModal(info) {
    if (!info) return null;

    const { object } = info;
    if (!object) {
      return null;
    }

    const { url, location } = object;
    if (!url || !location[0] || !location[1]) {
      return null;
    }

    return (
      <Popup
        tipSize={5}
        anchor="bottom"
        longitude={location[0]}
        latitude={location[1]}
        closeOnClick={false}
        offset={[0, -8]}
        onClose={() => setSelectedPOD(null)}
      >
        <div style={{ marginTop: '10px' }}>
          <img src={url} style={{ maxHeight: '100px' }} alt="" />
        </div>
      </Popup>
    );
  }

  return (
    <Grid size={{ xs: 12, sm: 6 }} sx={classes.bodyItemWrapper}>
      <Container sx={{ ...classes.bodyBottomItem, ...classes.boxWrapper, overflow: 'hidden' }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid size={7}>
            <Box sx={classes.grayTextWithMarginBtm}>ETA</Box>
            <Box sx={{ ...classes.boldText, ...(shouldHighlightDate && classes.redText) }} data-testid="map-eta">
              {etaInTime}
            </Box>
          </Grid>
          {settings.hide_delivery_vehicle_info !== 'true' && (
            <Grid size={5} style={{ textAlign: 'right' }}>
              <Box sx={classes.grayTextWithMarginBtm}>Delivery Vehicle</Box>
              {[SHIPMENT_STATUS.UNSERVICEABLE].includes(milestone) ? (
                <span>N/A</span>
              ) : (
                <ClickTooltip
                  open={openEcoTooltip}
                  onOpen={handleOpenEcoTooltip}
                  onClose={() => setOpenEcoTooltip(false)}
                  title={isEcoFriendly && delivery.extra_text?.eco_friendly}
                  dataTestId="map-vehicle"
                >
                  <Box
                    sx={{
                      ...classes.boldText,
                      textDecoration: isEcoFriendly ? 'underline' : undefined,
                      cursor: isEcoFriendly ? 'pointer' : undefined,
                    }}
                  >
                    <span>{isEcoFriendly && `🌱 `}</span>
                    <span>{!!driver ? `${capitalize(driver.vehicle_make)} ${capitalize(driver.vehicle_model)}` : 'N/A'}</span>
                  </Box>
                </ClickTooltip>
              )}
            </Grid>
          )}
        </Grid>
        {settings.tracking_show_map === 'true' && (
          <Box height={mediaQuery.isMobile ? 250 : 500} mt={2.5}>
            <ReactMapGL
              {...viewport}
              mapStyle={mapStyle}
              onMove={(evt) => handleViewStateChange({ viewState: evt.viewState, oldViewState: viewport })}
              onClick={handleMapClick}
              style={{ width: '100%', height: '100%' }}
              attributionControl={false}
            >
              <div style={{ position: 'absolute', right: 10, top: 0, zIndex: 1 }}>
                <ToggleMapPins classes={classes} store={store} mediaQuery={mediaQuery} />
              </div>
              <DeckGL ref={deckRef} layers={layers} viewState={viewport} style={{ pointerEvents: 'none' }} />
              {renderModal(selectedPOD)}
            </ReactMapGL>
          </Box>
        )}
        {settings.tracking_show_map !== 'true' && mediaQuery.isMobile && (
          <Box
            style={{
              marginTop: 35,
              height: 1,
              width: '100%',
              backgroundColor: '#ccc',
            }}
          />
        )}
      </Container>
    </Grid>
  );
}

export default compose(inject('store'), observer)(Map);
